import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import { InView } from 'react-intersection-observer'

import { PostRenderer } from 'components-ref/organisms'
import { prefetchImageToBlob } from 'helpers/mediaPrefetch'
import { collectStoryPrefetchUrls } from 'helpers/storyPrefetch'
import { getRole } from 'helpers/util'

import { type IProps } from './PostViewWrapper.types'

const INITIAL_RENDER_COUNT = 3

function safeStr(value: unknown): string | null {
  if (typeof value !== 'string') return null

  const trimmed = value.trim()

  return trimmed || null
}

function uniqueUrls(values: unknown[]): string[] {
  const seen = new Set<string>()
  const urls: string[] = []

  for (let i = 0; i < values.length; i++) {
    const url = safeStr(values[i])

    if (!url || seen.has(url)) continue

    seen.add(url)
    urls.push(url)
  }

  return urls
}

/**
 * PHOTOS
 *
 * Prefetch:
 * - full-size image
 * - thumbnail
 * - preview variants
 *
 * We want both the feed thumbnail and the full-resolution
 * image hot in cache so opening the fullscreen preview is fast.
 */
function getPhotoPrefetchUrls(media: any): string[] {
  return uniqueUrls([
    media?.url,
    media?.thumb_url,
    media?.preview?.src,
    media?.active_thumb,
    media?.preview_url,
    media?.thumbnail_src,
    media?.thumbnail
  ])
}

/**
 * VIDEOS
 *
 * Prefetch image-based thumbnail assets only.
 *
 * Do NOT prefetch media.url here because that may be an HLS
 * .m3u8 playlist rather than an image.
 */
function getVideoPrefetchUrls(media: any): string[] {
  return uniqueUrls([
    media?.active_thumb,
    media?.thumb_url,
    media?.preview?.src,
    media?.preview_url,
    media?.thumbnail_src,
    media?.thumbnail
  ])
}

/**
 * Collect all image assets for a post that should be warmed
 * in the shared blob cache.
 *
 * This includes:
 * - photos
 * - photo previews
 * - video thumbnails
 * - video preview thumbnails
 * - poll backgrounds
 * - goal backgrounds
 */
function collectPrefetchUrlsFromPost(post: any): string[] {
  const urls: string[] = []

  /**
   * PHOTOS
   *
   * Prefetch both the full image and thumbnail variants.
   */
  const photos = Array.isArray(post?.photos)
    ? post.photos
    : []

  for (let i = 0; i < photos.length; i++) {
    urls.push(
      ...getPhotoPrefetchUrls(photos[i])
    )
  }

  /**
   * PHOTO PREVIEWS
   *
   * Same treatment as normal photos.
   */
  const previewPhotos = Array.isArray(
    post?.photos_preview
  )
    ? post.photos_preview
    : []

  for (
    let i = 0;
    i < previewPhotos.length;
    i++
  ) {
    urls.push(
      ...getPhotoPrefetchUrls(
        previewPhotos[i]
      )
    )
  }

  /**
   * VIDEOS
   *
   * Only prefetch image thumbnails.
   * Do not fetch the HLS URL here.
   */
  const videos = Array.isArray(post?.videos)
    ? post.videos
    : []

  for (let i = 0; i < videos.length; i++) {
    urls.push(
      ...getVideoPrefetchUrls(videos[i])
    )
  }

  /**
   * VIDEO PREVIEWS
   *
   * Same treatment as regular videos:
   * thumbnails only.
   */
  const previewVideos = Array.isArray(
    post?.videos_preview
  )
    ? post.videos_preview
    : []

  for (
    let i = 0;
    i < previewVideos.length;
    i++
  ) {
    urls.push(
      ...getVideoPrefetchUrls(
        previewVideos[i]
      )
    )
  }

  /**
   * POLLS
   *
   * Prefetch BOTH:
   * - full background for fullscreen/modal view
   * - thumbnail for feed rendering
   */
  const pollBackground = safeStr(
    post?.poll?.background_src
  )

  const pollBackgroundThumb = safeStr(
    post?.poll?.background_src_thumb
  )

  if (pollBackground) {
    urls.push(pollBackground)
  }

  if (pollBackgroundThumb) {
    urls.push(pollBackgroundThumb)
  }

/**
 * GOALS
 *
 * Prefetch BOTH:
 * - full background for fullscreen/modal view
 * - thumbnail for feed rendering
 *
 * Matches the existing poll prefetch behavior.
 */
const goalBackground = safeStr(
    post?.goal?.background_src
)

const goalBackgroundThumb = safeStr(
    post?.goal?.background_src_thumb
)

if (goalBackground) {
    urls.push(goalBackground)
}

if (goalBackgroundThumb) {
    urls.push(goalBackgroundThumb)
}
  const stories = Array.isArray(post?.stories)
    ? post.stories
    : []

  urls.push(...collectStoryPrefetchUrls(stories))

  /**
   * Remove duplicates before starting any requests.
   */
  return uniqueUrls(urls)
}

const PostViewWrapperComponent = (
  props: IProps
) => {
  const {
    addDebounceImpression,
    lastPostRef,
    post,
    index,
    userData,
    dataQuery,
    isLastPost,
    hidePremiumBadge,
    hideComments,
    hidePinned
  } = props

  const {
    id: postId,
    user_id: postUserId
  } = post

  /**
   * First few posts should begin media preloading
   * immediately because they're above or close to
   * the initial viewport.
   */
  const aboveTheFold =
    index < INITIAL_RENDER_COUNT

  const [isInViewport, setIsInViewport] =
    useState(false)

  const [preloadMedia, setPreloadMedia] =
    useState<boolean>(aboveTheFold)

  /**
   * Prevent duplicate impression events for the
   * same mounted post.
   */
  const didImpressRef = useRef(false)

  /**
   * Track each prefetched URL individually.
   *
   * A post may gain additional URLs after cache/network
   * hydration, so a single didPrefetch boolean would
   * incorrectly prevent those new assets from loading.
   */
  const prefetchedUrlsRef = useRef<
    Set<string>
  >(new Set())

  /**
   * Build the complete set of assets this post
   * may need.
   */
  const prefetchUrls = useMemo(
    () =>
      collectPrefetchUrlsFromPost(post),
    [post]
  )

  /**
   * PREFETCH MEDIA
   *
   * Begins once:
   * - the post is above the fold, or
   * - the post enters the viewport.
   */
  useEffect(() => {
    if (!preloadMedia) return
    if (prefetchUrls.length === 0) return

    for (
      let i = 0;
      i < prefetchUrls.length;
      i++
    ) {
      const url = prefetchUrls[i]

      if (
        prefetchedUrlsRef.current.has(
          url
        )
      ) {
        continue
      }

      prefetchedUrlsRef.current.add(url)

      /**
       * Do not pass a local AbortSignal here.
       *
       * mediaPrefetch handles shared in-flight
       * requests itself.
       *
       * Allowing the request to finish means the
       * shared blob cache can still be populated
       * even if this virtualized row unmounts.
       */
      prefetchImageToBlob(url).catch(
        () => {
          /**
           * Allow a later retry.
           *
           * mediaPrefetch still controls its own
           * failure TTL, so this won't cause an
           * aggressive retry loop.
           */
          prefetchedUrlsRef.current.delete(
            url
          )
        }
      )
    }
  }, [
    preloadMedia,
    prefetchUrls
  ])

  /**
   * RESET VIRTUALIZED ROW STATE
   *
   * If virtualization ever reuses this component
   * instance for a different post, clear state that
   * belongs specifically to the previous row.
   */
  useEffect(() => {
    didImpressRef.current = false
    prefetchedUrlsRef.current.clear()

    setPreloadMedia(
      index < INITIAL_RENDER_COUNT
    )
  }, [
    postId,
    index
  ])

  /**
   * IMPRESSION TRACKING
   *
   * Fire at most once per mounted post.
   */
  const fireImpression = useCallback(() => {
    if (didImpressRef.current) return

    didImpressRef.current = true

    addDebounceImpression?.(
      postId,
      postUserId !== userData?.id
    )
  }, [
    addDebounceImpression,
    postId,
    postUserId,
    userData?.id
  ])

  return (
    <InView
      key={`post-view-${postId}`}
      as="div"
      threshold={[0.2, 0.4]}
      triggerOnce={false}
      onChange={(inView, entry) => {
        setIsInViewport(inView)

        if (!inView) return

        /**
         * Once a post comes into view, make sure
         * its media begins preloading.
         */
        setPreloadMedia(true)

        /**
         * Count an impression once at least 40%
         * of the post is visible.
         */
        if (
          entry.intersectionRatio >= 0.4
        ) {
          fireImpression()
        }
      }}
    >
      <div
        ref={
          isLastPost
            ? lastPostRef
            : null
        }
      >
        <PostRenderer
          postIndex={index}
          role={getRole(
            userData,
            post
          )}
          postId={postId}
          postData={post}
          dataQuery={dataQuery}
          userData={userData}
          hidePremiumBadge={
            hidePremiumBadge
          }
          hideComments={hideComments}
          preloadMedia={preloadMedia}
          hidePinned={hidePinned}
          isInViewport={isInViewport}
        />
      </div>
    </InView>
  )
}

export const PostViewWrapper = memo(
  PostViewWrapperComponent
)